import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { createRule, updateRule } from '@coparent/shared/src/services/ruleService';
import { RULE_CATEGORIES } from '@coparent/shared/src/utils/constants';
import ChildMultiSelect from '../common/ChildMultiSelect';
import { HiX, HiExclamation } from 'react-icons/hi';
import toast from 'react-hot-toast';

const RuleForm = ({ onClose, rule }) => {
  const { currentUser } = useAuth();
  const { familyId, familyChildren, childrenMap } = useFamily();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(rule?.title || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [category, setCategory] = useState(rule?.category || 'other');
  const [allChildren, setAllChildren] = useState(!rule?.childIds?.length);
  const [selectedChildIds, setSelectedChildIds] = useState(rule?.childIds || []);
  const isEditing = !!rule;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Rule title is required');
      return;
    }
    setLoading(true);
    try {
      const data = {
        title: title.trim(),
        description: description.trim() || null,
        category,
        childIds: allChildren ? [] : selectedChildIds,
      };

      if (isEditing) {
        await updateRule(familyId, rule.id, data);
        toast.success('Rule updated — pending re-approval');
      } else {
        data.proposedBy = currentUser.uid;
        await createRule(familyId, data);
        toast.success('Rule proposed!');
      }
      onClose();
    } catch (error) {
      toast.error(isEditing ? 'Failed to update rule' : 'Failed to propose rule');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-ink-900">{isEditing ? 'Edit Rule' : 'Propose Rule'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
            <HiX className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Rule</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input-base"
              placeholder='e.g., "No screens after 8pm"'
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Details (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-base"
              rows={3}
              placeholder="Explain the reasoning or specifics..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Category</label>
            <div className="flex gap-2 flex-wrap">
              {RULE_CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all duration-200 ${
                    category === cat.value
                      ? 'bg-ink-800 text-white shadow-warm-sm'
                      : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Applies to</label>
            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={() => { setAllChildren(true); setSelectedChildIds([]); }}
                className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all duration-200 ${
                  allChildren
                    ? 'bg-ink-800 text-white shadow-warm-sm'
                    : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
                }`}
              >
                All children
              </button>
              <button
                type="button"
                onClick={() => setAllChildren(false)}
                className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all duration-200 ${
                  !allChildren
                    ? 'bg-ink-800 text-white shadow-warm-sm'
                    : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
                }`}
              >
                Specific children
              </button>
            </div>
            {!allChildren && familyChildren.length > 0 && (
              <ChildMultiSelect
                familyChildren={familyChildren}
                childrenMap={childrenMap}
                selectedChildIds={selectedChildIds}
                onChange={setSelectedChildIds}
              />
            )}
          </div>

          {isEditing && rule.status === 'agreed' && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200/60 rounded-xl">
              <HiExclamation className="text-amber-500 text-base flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                Editing this rule will require your co-parent to re-approve it.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? 'Saving...' : (isEditing ? 'Save & Re-propose' : 'Propose Rule')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RuleForm;
